import { useEffect, useRef, useState } from 'react';
import { ApiError, type Api, type TurnView } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';

type Msg = { role: 'agent' | 'customer'; text: string; bridge?: boolean };

export const PreviewPanel = ({ api, t, agentId }: { api: Api; t: T; agentId: string }) => {
  // ADR-071: the text preview runs the agent's AI turn — an Agents action; without it the panel is not offered.
  const can = useCan();
  const [previewId, setPreviewId] = useState<string>();
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [turn, setTurn] = useState<TurnView>();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [ended, setEnded] = useState(false);
  const [error, setError] = useState<string>();
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), [msgs.length, busy]);

  const start = async () => {
    setBusy(true);
    setError(undefined);
    setEnded(false);
    try {
      const r = await api.openPreview(agentId);
      setPreviewId(r.previewId);
      setMsgs([{ role: 'agent', text: r.say }]);
      setTurn(r.turn);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    const m = text.trim();
    if (!m || !previewId || ended) return;
    setText('');
    setMsgs((x) => [...x, { role: 'customer', text: m }]);
    setBusy(true);
    try {
      const r = await api.sendPreview(previewId, m);
      setMsgs((x) => [...x, ...(r.turn.bridge?.used && r.turn.bridge.text ? [{ role: 'agent' as const, text: r.turn.bridge.text, bridge: true }] : []), { role: 'agent', text: r.say }]);
      setTurn(r.turn);
      setEnded(r.ended);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
    } finally {
      setBusy(false);
    }
  };

  const errText = (c: string) => (t(`error.${c}`) === `error.${c}` ? t('error.generic') : t(`error.${c}`));

  if (!can(PERM.agents)) return null;
  return (
    <div>
      <p className="muted">{t('preview.hint')}</p>
      {error && <div className="banner error">{errText(error)}</div>}
      {!previewId ? (
        <div className="empty">
          <button type="button" className="primary big" disabled={busy} onClick={() => void start()}>{busy ? t('studio.thinking') : t('preview.start')}</button>
        </div>
      ) : (
        <div className="studio-grid">
          <div className="chat">
            <div className="messages">
              {msgs.map((m, i) => (
                <div key={i} className={`bubble ${m.role === 'agent' ? 'assistant' : 'user'} ${m.bridge ? 'bridge' : ''}`}>{m.text}</div>
              ))}
              {busy && <div className="bubble assistant typing">{t('studio.thinking')}</div>}
              {ended && <div className="banner ok">{t('preview.ended')}</div>}
              <div ref={endRef} />
            </div>
            <form className="composer" onSubmit={(e) => { e.preventDefault(); void send(); }}>
              <textarea value={text} rows={2} placeholder={t('studio.placeholder')} disabled={ended} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }} />
              <button type="submit" className="primary" disabled={busy || ended || !text.trim()}>{t('studio.send')}</button>
            </form>
          </div>
          <aside className="draft">
            <div className="row-between">
              <h2>{t('preview.turn')}</h2>
              <button type="button" className="link" onClick={() => void start()}>{t('preview.restart')}</button>
            </div>
            {turn && (
              <dl className="turn">
                <dt>{t('preview.angle')}</dt><dd className="mono">{turn.angle ?? t('preview.none')}</dd>
                <dt>{t('preview.move')}</dt><dd className="mono">{turn.move}</dd>
                <dt>{t('preview.mood')}</dt><dd className="mono">{turn.mood ?? t('preview.none')}</dd>
                <dt>{t('preview.facts')}</dt><dd>{Object.keys(turn.facts).length ? Object.entries(turn.facts).map(([k, v]) => <div key={k} className="mono">{k} = {String(v)}</div>) : t('preview.none')}</dd>
                <dt>{t('preview.angles')}</dt><dd>{turn.anglesTried.length ? turn.anglesTried.map((a) => <div key={a.id} className="mono">{a.id} · {a.reaction}</div>) : t('preview.none')}</dd>
                <dt>{t('preview.critics')}</dt>
                <dd>{turn.critics.length ? turn.critics.map((c, i) => (
                  <div key={i} className={`critic ${c.verdict}`}>
                    <span className="mono">{c.id}</span> {c.verdict}{c.reason ? ` · ${c.reason}` : ''}
                    {c.detail?.map((d, j) => <div key={j} className="critic-detail">“{d}”</div>)}
                  </div>
                )) : t('preview.none')}</dd>
                <dt>{t('preview.bridge')}</dt><dd>{turn.bridge?.used ? `${turn.bridge.text} (${turn.bridge.source})` : t('preview.none')}</dd>
                <dt>{t('preview.ms')}</dt><dd className="mono">{turn.ms ?? '—'} ms</dd>
              </dl>
            )}
          </aside>
        </div>
      )}
    </div>
  );
};
